import React, { Component } from 'react';
import { func, object, string } from 'prop-types';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { getProNetworkPriceLabel } from '../../../util/proNetworkPricing';
import { getRentalDisplayPrice } from '../../../util/rentalPricing';
import { isExclusiveMapPin } from '../../../util/exclusiveMapPin';
import { isBookingProcessAlias } from '../../../transactions/transaction';

import css from './SearchMapPriceLabel.module.css';

class SearchMapPriceLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const currentDayRate = currentListing.attributes.publicData?.dayRate?.amount;
    const nextDayRate = nextListing.attributes.publicData?.dayRate?.amount;
    const currentDayRateCurrency = currentListing.attributes.publicData?.dayRate?.currency;
    const nextDayRateCurrency = nextListing.attributes.publicData?.dayRate?.currency;
    const hasSameDayRate =
      currentDayRate === nextDayRate && currentDayRateCurrency === nextDayRateCurrency;
    const hasSameOffer =
      !!currentListing.attributes.publicData?.offerDayRate ===
      !!nextListing.attributes.publicData?.offerDayRate;
    const hasSameTitle = currentListing.attributes.title === nextListing.attributes.title;
    const hasSameExclusiveMapPin =
      isExclusiveMapPin(currentListing) === isExclusiveMapPin(nextListing);
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;

    return !(
      isSameListing &&
      hasSamePrice &&
      hasSameDayRate &&
      hasSameOffer &&
      hasSameTitle &&
      hasSameExclusiveMapPin &&
      hasSameActiveStatus &&
      hasSameRefreshToken
    );
  }

  render() {
    const {
      className,
      rootClassName,
      intl,
      listing,
      onListingClicked,
      isActive,
      config,
    } = this.props;
    const currentListing = ensureListing(listing);
    const { price, publicData, title } = currentListing.attributes;
    const exclusiveMapPin = isExclusiveMapPin(currentListing);
    const proNetworkPriceLabel = getProNetworkPriceLabel(currentListing, intl);
    const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
    const rentalDisplay = isBookable
      ? getRentalDisplayPrice(currentListing)
      : { money: price };
    const displayMoney = rentalDisplay.money || price;

    // Create formatted price if currency is known or alternatively show just the unknown currency.
    const formattedPrice = exclusiveMapPin
      ? title || null
      : proNetworkPriceLabel
        ? proNetworkPriceLabel
        : displayMoney && displayMoney.currency === config.currency
          ? formatMoney(intl, displayMoney)
          : displayMoney?.currency
            ? displayMoney.currency
            : null;

    const classes = classNames(rootClassName || css.root, className);
    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !formattedPrice,
      [css.pendingLabel]: currentListing.attributes.publicData?.pending,
    });
    const caretClasses = classNames(css.caret, {
      [css.caretActive]: isActive,
      [css.pendingCaret]: currentListing.attributes.publicData?.pending,
    });

    return (
      <button className={classes} onClick={() => onListingClicked(currentListing)}>
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>{formattedPrice}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

SearchMapPriceLabel.defaultProps = {
  className: null,
  rootClassName: null,
};

SearchMapPriceLabel.propTypes = {
  className: string,
  rootClassName: string,
  listing: propTypes.listing.isRequired,
  onListingClicked: func.isRequired,
  config: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SearchMapPriceLabel);
